// The emails an RFP will actually send, which is what step 6 reviews.
//
// A recipient is a person; an email is a firm. Two sales contacts at one GA are
// two copies of ONE email, not two emails, and the old flat contact list could
// not say that — it gave them two preview tabs with the same label.
//
// Grouping only. Selection is still by contact id, and who receives what is
// still decided by buildRfpRecipients.

import { isAgencyCopy } from "@shared/utils/rfpGaCarriers";
import type { RfpEmailRecipient } from "./rfpRecipients";

export type OutgoingEmailKind = "ga" | "carrier";

export type OutgoingEmail = {
  /** Stable across renders, and what the left column selects on. */
  key: string;
  kind: OutgoingEmailKind;
  title: string;
  /** Everyone who could be ticked for this email. */
  candidates: RfpEmailRecipient[];
  /** Those actually ticked. Empty means this email will not be sent. */
  ticked: RfpEmailRecipient[];
  /** Coverage lines this email asks about, merged across its recipients. */
  coverageTypes: string[];
};

export type BuildOutgoingEmailsInput = {
  recipients: RfpEmailRecipient[];
  usingGa: boolean;
  selectedIds: Set<string>;
  /**
   * Recipients added by hand at the preview. They are always sent, so they are
   * always "ticked", and they fold into the firm they were added for when they
   * carry one.
   */
  customRecipients?: RfpEmailRecipient[];
};

/**
 * Colleagues, who are CC'd on every email rather than sent one of their own.
 * The server decides this with the same `isAgencyCopy`, so the two cannot
 * disagree about who is being asked to quote and who is just on copy.
 */
export function agencyCopyRecipients(input: BuildOutgoingEmailsInput): RfpEmailRecipient[] {
  return [...input.recipients, ...(input.customRecipients ?? [])].filter(isAgencyCopy);
}

export function buildOutgoingEmails(input: BuildOutgoingEmailsInput): OutgoingEmail[] {
  const emails = new Map<string, OutgoingEmail>();
  const push = (recipient: RfpEmailRecipient, alwaysTicked: boolean) => {
    if (isAgencyCopy(recipient)) return;
    const key = input.usingGa
      ? `ga:${recipient.generalAgencyId}`
      : `carrier:${recipient.carrierId}`;
    let email = emails.get(key);
    if (!email) {
      email = {
        key,
        kind: input.usingGa ? "ga" : "carrier",
        // carrierName holds the GA's name in GA mode, which is why it works for
        // both.
        title: recipient.carrierName || recipient.name,
        candidates: [],
        ticked: [],
        coverageTypes: [],
      };
      emails.set(key, email);
    }
    email.candidates.push(recipient);
    if (alwaysTicked || input.selectedIds.has(recipient.id)) email.ticked.push(recipient);
    for (const coverageType of recipient.coverageTypes) {
      if (!email.coverageTypes.includes(coverageType)) email.coverageTypes.push(coverageType);
    }
  };

  for (const recipient of input.recipients) push(recipient, false);
  // Hand-added recipients are sent whether or not anything is ticked, so they
  // count as ticked and their group is never "this will not be sent".
  for (const recipient of input.customRecipients ?? []) push(recipient, true);

  return Array.from(emails.values());
}

/** The emails that would go nowhere. Empty means everything has a recipient. */
export function emailsWithNoRecipient(emails: OutgoingEmail[]): OutgoingEmail[] {
  return emails.filter((email) => email.ticked.length === 0);
}
